import { Record as record, Set, OrderedMap, Map, List } from 'immutable';
import { sum } from '../../helpers/Immutable';

import { construct, resolve, resolveAll } from '../../helpers/LinkMapper';

import { PlusBusDirections, JourneyDirections, FareCategories } from '../../constants/AppConstants';
import Order from '../Order';
import OrderTrip from '../order/Trip';
import OrderFare from '../order/Fare';
import PassengerRailcard from '../PassengerRailcard';
import Fares from '../Fares';
import Splits from '../Splits';
import { serialiseSplitTrips, serialiseTrip } from '../../services/OrderService';
import { generateUuidV4 } from '../../utils/fp-utils';
import { isSplitLegReturnDirection, isCombinedFareTicket } from '../../utils/splits';
import FeatureFlagStore from '../../stores/FeatureFlagStore';
import RailcardsStore from '../../stores/RailcardsStore';
import { SPLIT_TRIP_PASSENGER_COUNT_MISMATCH_MESSAGE } from '../../errors/Order';

const matchesPlusbusTicket = ticket => plusBusTicket => {
    const sameAdults = plusBusTicket.adults === ticket.adults;
    const sameChildren = plusBusTicket.children === ticket.children;
    const passengersMatch = sameAdults || sameChildren;
    const ticketHasRailcard = !!ticket.railcard;
    const plusBusHasRailcard = !!plusBusTicket.railcard;

    if (ticketHasRailcard && !plusBusHasRailcard) {
        return false;
    } else if (!ticketHasRailcard && plusBusHasRailcard) {
        return false;
    } else if (ticketHasRailcard && plusBusHasRailcard) {
        return (
            passengersMatch &&
            ticket.railcard.code === plusBusTicket.railcard.code &&
            !plusBusTicket.isRefunded
        );
    }

    return passengersMatch && !plusBusTicket.isRefunded;
};

const matchesPlusbusFare = (fare, trip) => plusBus => {
    const outwardMatches = trip
        .getPlusBus()
        .matchesOriginOrDestination(plusBus, PlusBusDirections.OUTWARD);
    const inwardMatches = trip
        .getPlusBus()
        .matchesOriginOrDestination(plusBus, PlusBusDirections.RETURN);
    const sameDay = fare.getValidFrom().diff(plusBus.getValidFrom(), 'days') === 0;

    return (outwardMatches || inwardMatches) && sameDay && plusBus.hasAmendableTickets();
};

function fareSetter(fares, fare, ticket) {
    if (!ticket) {
        return fares;
    }

    if (fares.has(fare)) {
        return fares.set(fare, fares.get(fare).add(ticket));
    }

    return fares.set(fare, Set.of(ticket));
}

function fareRemover(fares, fare, ticket) {
    fares = fares.set(fare, fares.get(fare).remove(ticket));

    if (fares.get(fare).size === 0) {
        return fares.remove(fare);
    }

    return fares;
}

const defaults = {
    order: null,
    trip: null,
    trips: new List([new OrderTrip()]),
    fares: new OrderedMap(),
};

class AmendRequest extends record(defaults) {
    static fromJSON(json) {
        return construct(AmendRequest, json, {
            order: resolve.as(Order),
            trips: resolveAll.as(OrderTrip),
            fare: resolve.as(OrderFare),
        });
    }

    isValid() {
        const fares = this.fares.keySeq().toSet();
        return !!fares.size && fares.every(fare => !this.getFareWarnings(fare));
    }

    // The fare selected for `direction` on `trip` (given the tickets currently selected in
    // this.fares and the order's other trips, orderTrips, needed to resolve split cross-
    // references) - or null if `trip` isn't the leg selected for that direction. Shared by
    // hasOutwardFare/hasReturnFare/getSelectedComposition - they differ only in what they do
    // with the result (check truthiness vs read out the fare). Returning the actual matched
    // fare - rather than a boolean the caller re-derives a fare from separately - guarantees
    // getSelectedComposition never reads a different fare than the one that was actually found
    // in this.fares.
    _getSelectedFareForDirection(trip, direction, orderTrips) {
        const { fares } = this;
        if (direction === JourneyDirections.OUTWARD) {
            const isRegularMatch = !trip.isSplitTrip() && fares.has(trip.fares.outwardSingle);
            const isSplitMatch =
                trip.isSplitTrip() &&
                !trip.isPartOfSplitAsRegularReturnSingle() &&
                !isSplitLegReturnDirection(trip, orderTrips) &&
                fares.has(trip.fares.outwardSingle);
            return isRegularMatch || isSplitMatch ? trip.fares.outwardSingle : null;
        }
        // Unlike the outward regular check, these aren't gated on !isSplitTrip() - a split
        // trip's returnSingle/return fare can still be the one directly selected (e.g. a
        // return-only split leg with no cross-reference id), so they must be checked
        // independently rather than branching exclusively on isSplitTrip().
        if (fares.has(trip.fares.returnSingle)) {
            return trip.fares.returnSingle;
        }
        if (fares.has(trip.fares.return)) {
            return trip.fares.return;
        }
        const isSplitMatch =
            trip.isSplitTrip() &&
            (trip.isPartOfSplitAsRegularReturnSingle() ||
                isSplitLegReturnDirection(trip, orderTrips)) &&
            fares.has(trip.fares.outwardSingle);
        return isSplitMatch ? trip.fares.outwardSingle : null;
    }

    hasOutwardFare() {
        const { trips, fares, order } = this;
        if (!fares || !trips) {
            return false;
        }
        return trips.some(
            trip =>
                !!this._getSelectedFareForDirection(trip, JourneyDirections.OUTWARD, order?.trips),
        );
    }

    hasReturnFare() {
        const { trips, fares, order } = this;
        if (!fares || !trips) {
            return false;
        }
        return trips.some(
            trip =>
                !!this._getSelectedFareForDirection(trip, JourneyDirections.INWARD, order?.trips),
        );
    }

    // Both directions' current, as-booked journeys - used as fallback timing references for
    // whichever direction isn't being amended, since its trip's own journey field on the
    // in-progress Trip record never gets set in that case (nothing dispatches a selection for a
    // step that never renders). Computed together, rather than as two direction-specific calls,
    // since a split trip's underlying merge produces both directions in one pass (see
    // Order#getJourneysForBothDirections).
    getUnchangedJourneys() {
        const { outward, inward } = this.order?.getJourneysForBothDirections(this.trips?.first()) || {};
        return { outward: outward || null, inward: inward || null };
    }

    // Composition of the tickets selected for supersession on one direction - every section of
    // a direction has the same selected composition, so reading it off one matching trip is
    // enough. Returns null when nothing is selected for that direction.
    //
    // `currentRailcards` is the amend "choose date" step's own railcard stepper state (a
    // List<PassengerRailcard> of physical instances, as sent to the journey search) - pass it
    // when known so both railcardCode and railcards reflect a railcard added/removed/swapped
    // there instead of the step-1 ticket selection, which the search step never updates. Without
    // it, both fall back to that step-1 selection (e.g. for callers outside the search/results
    // flow).
    getSelectedComposition(direction, currentRailcards) {
        const { trips, fares, order } = this;
        if (!fares || !trips) {
            return null;
        }

        let selectedFare = null;
        const matchingTrip = trips.find(trip => {
            selectedFare = this._getSelectedFareForDirection(trip, direction, order?.trips);
            return !!selectedFare;
        });
        if (!matchingTrip) {
            return null;
        }

        return {
            adults: this.getAdults(selectedFare),
            children: this.getChildren(selectedFare),
            railcardCode: currentRailcards
                ? this._getRailcardCodeFromInstances(currentRailcards)
                : this.getSelectedRailcardCode(selectedFare),
            railcards: currentRailcards
                ? this._getRailcardsFromInstances(currentRailcards)
                : this.getSelectedRailcards(selectedFare),
        };
    }

    // The single railcard code shared by every selected ticket of `fare` ('' when none of them
    // carry one), or null when the selection mixes more than one railcard - in that ambiguous
    // case there's no single code a candidate ticket line could be matched against.
    getSelectedRailcardCode(fare) {
        const railcardCodes = this.fares
            .get(fare)
            .map(ticket => ticket.railcard?.code || '')
            .toSet();
        return railcardCodes.size === 1 ? railcardCodes.first() : null;
    }

    // getSelectedRailcardCode's counterpart for a List<PassengerRailcard> (e.g. the amend search
    // step's own railcard state) rather than a fare's selected tickets: '' when it's empty (no
    // railcard requested), the shared code when every entry uses the same one, or null when it
    // mixes more than one - same ambiguous-match rule as getSelectedRailcardCode.
    _getRailcardCodeFromInstances(railcards) {
        if (railcards.isEmpty()) {
            return '';
        }
        const codes = railcards.map(railcard => railcard.code).toSet();
        return codes.size === 1 ? codes.first() : null;
    }

    // getSelectedRailcards' counterpart for a List<PassengerRailcard> of physical instances (the
    // amend search step's own railcard state): converts each entry's instance quantity into the
    // passenger-count quantity a mixed-composition breakdown needs (see
    // _getRequiredRailcardBreakdown), using that railcard type's own capacity (maxAdults +
    // maxChildren) from RailcardsStore - one physical card can cover more than one passenger
    // (e.g. a Two Together or Family & Friends Railcard), so instances and passengers aren't
    // interchangeable. Falls back to treating the instance quantity as the passenger count when
    // the type isn't in RailcardsStore's cache - an approximate breakdown beats none.
    _getRailcardsFromInstances(railcards) {
        return railcards.map(railcard => {
            const type = this._getRailcardType(railcard.code);
            const capacityPerInstance = type ? (type.maxAdults + type.maxChildren) || 1 : 1;
            return new PassengerRailcard({
                code: railcard.code,
                quantity: railcard.quantity * capacityPerInstance,
            });
        });
    }

    // RailcardsStore.get() reads straight off its cache Map, which is only created once the
    // async RAILCARDS_LOADED action has fired at least once - calling it any earlier throws
    // rather than returning undefined. The amend search step can be reached before that load
    // resolves, so this null-guards the lookup instead of letting it crash the composition.
    _getRailcardType(code) {
        try {
            return RailcardsStore.get(code);
        } catch {
            return null;
        }
    }

    hasSplits() {
        const hasSplitTrips = this.trips?.some(trip => trip?.isSplitTrip());

        return hasSplitTrips;
    }

    // Composition to seed the ONE shared amend search with, for both split and non-split
    // amends alike: the first selected fare's own composition. Requesting more than one
    // passenger type at once makes every candidate fare come back bundled per type, which
    // breaks the non-split route/price match (Fares.filteredByRoute -> getClosestPriceListFromFares
    // compares against a candidate's bundled totalPrice, not a single ticket's price) - so this
    // stays single-type even though a split amend can have a different composition selected per
    // direction; that per-direction composition is enforced separately by getFilteredFaresForAmend.
    // "first selected" relies on `fares` being an OrderedMap (see defaults above) - a plain Map's
    // iteration order isn't guaranteed to match insertion order.
    //
    // NOTE: unlike getSelectedComposition's `railcards` (passenger-count quantity, see
    // getSelectedRailcards), this composition's `railcards.quantity` is a physical-railcard-
    // instance count (see getSelectedRailcardInstances) - the shape the journey search and the
    // amend "choose date" railcard stepper actually need.
    getSearchComposition() {
        const fare = this.fares.keySeq().toList().filter(fareKey => !fareKey.isPlusBus()).first();
        return {
            adults: this.getAdults(fare),
            children: this.getChildren(fare),
            railcards: this.getSelectedRailcardInstances(fare),
        };
    }

    // The valid, per-code RailcardTickets groups among `fare`'s own currently-selected tickets,
    // read off a single fare (like getAdults/getChildren above) rather than summed across every
    // selected fare - a passenger who appears on more than one selected fare (e.g. both legs of
    // a return) would otherwise be counted once per fare instead of once overall. Shared by
    // getSelectedRailcards (passenger-count quantity, for candidate-composition matching) and
    // getSelectedRailcardInstances (physical-card-count quantity, for the journey search).
    _getValidRailcardTicketsForFare(fare) {
        const selectedTickets = this.fares.get(fare);
        if (!selectedTickets) {
            return new List();
        }
        return fare
            .getRailcards(selectedTickets)
            .filter(railcard => railcard.code)
            .map(railcard => fare.getTicketsWithRailCard(railcard, selectedTickets))
            .filter(railcardTickets => railcardTickets.isValid())
            .toList();
    }

    // Railcard composition for `fare`'s selected tickets, quantity = passengers covered per
    // code - used to check a candidate replacement's own ticket breakdown matches exactly (see
    // _getRequiredRailcardBreakdown), not to tell the journey search how many physical railcards
    // exist (see getSelectedRailcardInstances for that - the two differ whenever a railcard's own
    // maxAdults/maxChildren is more than 1, e.g. a Veterans Railcard covering 2 adults needs only
    // 1 physical card, not a "quantity" of 2).
    getSelectedRailcards(fare) {
        return this._getValidRailcardTicketsForFare(fare).map(railcardTickets => new PassengerRailcard({
            code: railcardTickets.railcard.code,
            quantity: railcardTickets.adults + railcardTickets.children,
        }));
    }

    // Railcard composition for `fare`'s selected tickets, quantity = physical railcard instances
    // needed per code (RailcardTickets#railcardCount) - what the journey search's `railcards`
    // array and the amend "choose date" railcard stepper actually need. See getSelectedRailcards
    // for why this can't just replace it.
    getSelectedRailcardInstances(fare) {
        return this._getValidRailcardTicketsForFare(fare).map(railcardTickets => new PassengerRailcard({
            code: railcardTickets.railcard.code,
            quantity: railcardTickets.railcardCount,
        }));
    }

    getFareWarnings(fare) {
        const selectedTickets = this.fares.get(fare);
        const remainingTickets = fare.tickets.toSet().subtract(selectedTickets);
        const railcards = fare.getRailcards(remainingTickets);
        let warnings = railcards.reduce((result, railcard) => {
            const railcardTickets = fare.getTicketsWithRailCard(railcard, remainingTickets);
            return railcardTickets.isValid()
                ? result
                : result.add(`account.orders.refund.warning.${railcard.code}`);
        }, new Set());

        return warnings.size ? warnings : null;
    }

    setOrder(order) {
        return this.set('order', order);
    }

    transformFares() {
        return this.fares
            .keySeq()
            .toSet()
            .map(item => item.set('tickets', this.fares.get(item).toList))
            .toList();
    }

    setPlusBusTickets(plusBusFares, ticket, fares) {
        // Automaticaly select the equivalent plusbus fare for a given
        // ticket. When amending, it's like for like, so if a fare has
        // an equivalent plusbus fare that must be selected too. We
        // filter out so that only adults or children are selected.
        let next = fares;
        plusBusFares.map(plusBusFare => {
            const selectedTickets = fares.get(plusBusFare);
            const plusBusTicket = plusBusFare.tickets
                .toSet()
                .subtract(selectedTickets)
                .filter(matchesPlusbusTicket(ticket))
                .first();
            next = fareSetter(next, plusBusFare, plusBusTicket);
        });

        return next;
    }

    removePlusBusTicket(plusBusFares, ticket, fares) {
        let next = fares;
        plusBusFares.map(plusBusFare => {
            const selectedTickets = fares.get(plusBusFare);
            const plusBusTicket = selectedTickets.filter(matchesPlusbusTicket(ticket)).first();
            next = fareRemover(next, plusBusFare, plusBusTicket);
        });
        return next;
    }

    getTripByTicketId(ticketId) {
        if (this.order && this.order.trips) {
            const orderTrip = this.order.trips.find(trip => trip.getFareByTicketId(ticketId));
            if (orderTrip) {
                return orderTrip;
            }
        }
        return null;
    }

    addTicket(fare, ticket) {
        let fares = fareSetter(this.fares, fare, ticket);

        let trip = this.getTripByTicketId(ticket.id);
        if (!trip && this.trip?.hasPlusBus()) {
            trip = this.trip;
        }

        if (trip?.hasPlusBus()) {
            const plusBusFares = trip.getPlusBusFares().filter(matchesPlusbusFare(fare, trip));
            fares = this.setPlusBusTickets(plusBusFares, ticket, fares);
        }

        let next = this.set('fares', fares);
        if (!trip) {
            return next;
        }

        return next.set(
            'trips',
            this.trips.find(t => t.id === trip.id) ? this.trips : this.trips.push(trip),
        );
    }

    removeTicket(fare, ticket) {
        let fares = fareRemover(this.fares, fare, ticket);

        let trip = this.getTripByTicketId(ticket.id);
        if (!trip && this.trip?.hasPlusBus()) {
            trip = this.trip;
        }

        if (trip?.hasPlusBus()) {
            const plusBusFares = trip.getPlusBusFares().filter(matchesPlusbusFare(fare, trip));
            fares = this.removePlusBusTicket(plusBusFares, ticket, fares);
        }

        let next = this.set('fares', fares);
        if (!trip) {
            return next;
        }

        // keep the trip while any of its tickets remain selected
        const hasRemainingTickets = fares.some(tickets =>
            tickets.some(selected => trip.getFareByTicketId(selected.id)),
        );
        if (hasRemainingTickets) {
            return next;
        }

        return next.set(
            'trips',
            this.trips.filter(t => t.id !== trip.id),
        );
    }

    getAdults(fare) {
        return sum(this.fares.get(fare), 'adults');
    }

    getChildren(fare) {
        return sum(this.fares.get(fare), 'children');
    }

    totalAdminFee(config) {
        const ticketCount = this.getTicketCount();
        return ticketCount * config.adminFeePerTicket + config.adminFeePerOrder;
    }

    getAmendedTickets() {
        return this.fares.toList().flatten(1);
    }

    getAmendedTicketIds() {
        return this.getAmendedTickets().map(ticket => ticket.id);
    }

    getTicketCount() {
        return this.fares
            .keySeq()
            .filter(x => !x.isPlusBus())
            .map(x => this.fares.get(x))
            .toList()
            .flatten(1).size;
    }

    isSelected(fare) {
        return this.fares.has(fare);
    }

    // fareTicketPairs is an array of { fare, ticket } pairs representing one passenger
    // across every leg of a split journey (e.g. their ticket on each split segment).
    isPassengerSelected(fareTicketPairs) {
        return fareTicketPairs.every(({ fare, ticket }) => {
            const selectedTickets = this.fares.get(fare);
            return selectedTickets ? selectedTickets.contains(ticket) : false;
        });
    }

    isPassengerDisabledForAmend(fareTicketPairs, { isPlusBus, isAmendableFareType }) {
        return (
            isPlusBus ||
            !isAmendableFareType ||
            fareTicketPairs.some(({ ticket }) => !ticket.canAmend() || ticket.amended) ||
            fareTicketPairs.some(({ ticket }) => isCombinedFareTicket(ticket))
        );
    }

    isAmendingInwardReturnOnly() {
        return this.hasReturnFare() && !this.hasOutwardFare();
    }

    // this method only returns an accurate description once the request's order has been updated with new trip
    getAmendmentDescription(trip) {
        const returnTrip = this.trips.find(t => t.isReturn());
        if (!returnTrip) {
            return 'single-outward';
        }

        const amendedPortion = this.order.trips.last().get('fares');
        const amendedOutward = amendedPortion.get('outwardSingle');
        const amendedReturn = amendedPortion.get('returnSingle');
        const originalOutward = trip.fares.outwardSingle;

        if (amendedOutward && amendedReturn) {
            return 'return-both';
        }

        if (!amendedOutward || !originalOutward) {
            console.error('Amend bookings description not found');
            return '';
        }

        const amendedOrigin = amendedOutward.origin.nlc;
        const amendedDestination = amendedOutward.destination.nlc;
        const originalOrigin = originalOutward.origin.nlc;
        const originalDestination = originalOutward.destination.nlc;

        if (amendedOrigin === originalOrigin && amendedDestination === originalDestination) {
            return 'return-outward';
        }
        if (amendedOrigin === originalDestination && amendedDestination === originalOrigin) {
            return 'return-inward';
        }
        console.error('Amend bookings description not found');
        return '';
    }

    getTotalPrice(direction) {
        const fares = this.fares.keySeq().toSet();
        const fare = direction === 'inward' ? fares.last() : fares.first();
        const tickets = this.fares.get(fare);
        return sum(tickets, 'price');
    }

    getHighestTicketPrice(direction) {
        const fares = this.fares.keySeq().toSet();
        const fare = direction === 'inward' ? fares.last() : fares.first();
        const tickets = this.fares.get(fare).sortBy(ticket => ticket.price);
        return tickets.last().price;
    }

    // Route/price/ticketType-narrowed candidate fares for a single (non-split) trip's
    // direction - a split trip shows all fares unfiltered instead (composition filtering alone
    // decides what's offered there), see getFilteredFaresForAmend.
    _getRouteFilteredFares(fares, selectedReturnSingle, servicePrice) {
        const trip = this.trips.first();
        const originalFare = selectedReturnSingle
            ? trip.fares.returnSingle || trip.fares.return
            : trip.getOutwardFare();
        return fares.filteredByRoute(
            originalFare.route,
            originalFare.getMatchingTicketPrice(servicePrice),
            originalFare.ticketType.code,
        );
    }

    // Whether `selectedComposition` mixes more than one railcard/no-railcard passenger - there's
    // no single shared code (getSelectedRailcardCode returned null) but real railcards are still
    // present. Such a selection has no single code to match candidates against, so it needs the
    // full per-code breakdown instead (see _getRequiredRailcardBreakdown).
    _isMixedRailcardComposition(selectedComposition) {
        return selectedComposition.railcardCode === null && selectedComposition.railcards?.size > 0;
    }

    // Per-railcard-code passenger counts (adults+children) across `tickets`, keyed by code
    // ('' for no railcard) - the shape a mixed-composition candidate is checked against.
    _getRailcardBreakdown(tickets) {
        return tickets.reduce((breakdown, ticket) => {
            const passengers = ticket.adults + ticket.children;
            return breakdown.update(
                ticket.railcard?.code || '',
                0,
                quantity => quantity + passengers,
            );
        }, new Map());
    }

    // The per-railcard-code breakdown `selectedComposition` actually requires: its railcards
    // list, plus whatever adults/children aren't covered by a real railcard (the '' bucket).
    _getRequiredRailcardBreakdown(selectedComposition) {
        const holderCounts = selectedComposition.railcards.reduce(
            (total, railcard) => total + railcard.quantity,
            0,
        );
        const noRailcardCount =
            selectedComposition.adults + selectedComposition.children - holderCounts;
        const required = selectedComposition.railcards.reduce(
            (breakdown, railcard) => breakdown.update(
                railcard.code,
                0,
                quantity => quantity + railcard.quantity,
            ),
            new Map(),
        );
        return noRailcardCount > 0 ? required.set('', noRailcardCount) : required;
    }

    // Whether `tickets` can stand in as `fare`'s replacement for `selectedComposition`: a mixed
    // selection must reproduce its exact per-railcard-code breakdown (see
    // _getRequiredRailcardBreakdown), since headcount alone can't tell a bundled candidate
    // apart from one that wrongly spreads a passenger's railcard onto a companion without one.
    //
    // Otherwise (a single shared code, or none at all) a candidate is offered only if its
    // tickets add up to exactly the selected passengers - otherwise the customer could pick a
    // replacement for the wrong physical passenger's ticket (e.g. a child fare replacing an
    // amended adult ticket), which cocs's own composition guard (validateNewSplitTrip) rejects
    // after the fact anyway. A candidate fare can bundle ticket lines for more than one
    // passenger type (e.g. a search for both an adult and a child returns fares priced per type
    // within the same fare, not just pure single-type fares), and a multi-passenger composition
    // can itself come back as several same-type lines rather than one bundled line (e.g. two
    // adults each on their own ticket) - so matching prefers a single line that already equals
    // the composition exactly (the common bundled-fare shape), and only falls back to summing
    // every candidate line that doesn't individually exceed the composition when no single line
    // does. Summing unconditionally would double-count a fare that happens to carry both shapes
    // (one bundled line plus separate per-passenger lines for the same passengers), wrongly
    // excluding it as an overshoot.
    _matchTicketsToComposition(fare, tickets, selectedComposition) {
        if (this._isMixedRailcardComposition(selectedComposition)) {
            const matchesBreakdown = this._getRailcardBreakdown(tickets).equals(
                this._getRequiredRailcardBreakdown(selectedComposition),
            );
            return matchesBreakdown
                ? fare.merge({ tickets, totalPrice: sum(tickets, 'price') })
                : null;
        }
        const singleMatch = tickets.find(
            ticket =>
                ticket.adults === selectedComposition.adults &&
                ticket.children === selectedComposition.children,
        );
        if (singleMatch) {
            return fare.merge({ tickets: new List([singleMatch]), totalPrice: singleMatch.price });
        }
        const isExactMatch =
            sum(tickets, 'adults') === selectedComposition.adults &&
            sum(tickets, 'children') === selectedComposition.children;
        return isExactMatch ? fare.merge({ tickets, totalPrice: sum(tickets, 'price') }) : null;
    }

    // `fare` narrowed to just the ticket line(s) that reproduce `selectedComposition`, or null
    // if nothing on it does. See _matchTicketsToComposition for the matching rules, and the
    // declined-railcard fallback below for why a single-code miss gets a second attempt.
    _getMatchingFareForComposition(fare, selectedComposition) {
        if (!selectedComposition) {
            return fare;
        }
        const withinComposition = ticket =>
            ticket.adults <= selectedComposition.adults &&
            ticket.children <= selectedComposition.children;
        const matchesRailcard = ticket =>
            selectedComposition.railcardCode === null ||
            (ticket.railcard?.code || '') === selectedComposition.railcardCode;

        const withinCompositionTickets = fare.tickets.filter(withinComposition);
        const railcardMatched = this._isMixedRailcardComposition(selectedComposition)
            ? withinCompositionTickets
            : withinCompositionTickets.filter(matchesRailcard);
        const strictResult = this._matchTicketsToComposition(
            fare,
            railcardMatched,
            selectedComposition,
        );
        if (strictResult || !selectedComposition.railcardCode) {
            return strictResult;
        }

        // The backend can decline a requested railcard for some or all of the amended
        // passengers within the same candidate fare, returning plain (no-railcard) tickets
        // for the declined portion instead - fall back to combining those with whatever
        // railcard-matching lines did come back, rather than rejecting a genuinely-available
        // service just because it doesn't fully carry a railcard the backend itself declined.
        const declinedFallbackTickets = railcardMatched.concat(
            withinCompositionTickets.filter(ticket => !ticket.railcard?.code),
        );
        return this._matchTicketsToComposition(fare, declinedFallbackTickets, selectedComposition);
    }

    // A split option narrowed by applying _getMatchingFareForComposition to each of its legs,
    // or null if any leg has nothing matching `selectedComposition` (a split can't be offered
    // with only some of its legs replaced).
    _getMatchingSplitForComposition(split, selectedComposition) {
        if (!selectedComposition) {
            return split;
        }
        const matchedFares = split.fares.map(fare =>
            this._getMatchingFareForComposition(fare, selectedComposition),
        );
        return matchedFares.every(Boolean) ? split.set('fares', matchedFares) : null;
    }

    // Decides which fares/splits to show while amending: for a single (non-split)
    // trip, fares are narrowed to the original fare's route/price/ticket type;
    // for a split trip, all fares are shown unfiltered. Splits are always
    // narrowed to singles, and only surfaced when split ticket amends are enabled.
    getFilteredFaresForAmend(
        fares,
        service,
        enableSplitTicketAmends,
        direction,
        selectedComposition = this.getSelectedComposition(direction),
    ) {
        const { trips } = this;
        const selectedReturnSingle =
            this.isAmendingInwardReturnOnly() || direction === JourneyDirections.INWARD;
        const servicePrice = this.getHighestTicketPrice(direction);

        const isSplits = trips.size > 1 || this.hasSplits(); // a split trip may have only 1 leg currently selected
        const filteredFares = isSplits
            ? fares
            : this._getRouteFilteredFares(fares, selectedReturnSingle, servicePrice);

        const filteredSinglesFares = filteredFares
            ? filteredFares.set(
                  'singles',
                  filteredFares
                      .get('singles')
                      .filterNot(Fares.isReturn)
                      .map(fare => this._getMatchingFareForComposition(fare, selectedComposition))
                      .filter(Boolean),
              )
            : filteredFares;

        const filteredSplitsSingles = service?.splits
            ? service.splits
                  .get('singles')
                  .filterNot(Splits.isReturn)
                  .map(split => this._getMatchingSplitForComposition(split, selectedComposition))
                  .filter(Boolean)
            : new Splits().get('singles');

        const filteredService = service?.splits
            ? service.set('splits', service.splits.set('singles', filteredSplitsSingles))
            : service;

        const splits = enableSplitTicketAmends
            ? filteredSplitsSingles
            : new Splits().get('singles');

        return { filteredSinglesFares, filteredService, splits };
    }

    getCategoryFilteredFares(service) {
        return FeatureFlagStore.get('enableAdvanceToWalkUpAmends')
            ? service.fares
            : service.fares.filteredByCategory(FareCategories.ADVANCE);
    }

    // Whether a service has any fare/split that would actually render on the
    // "choose fare" step - reuses getFilteredFaresForAmend (the source of truth for what
    // that step shows) so services with no real options can be hidden earlier, rather than
    // relying on a separately-computed price that can be non-zero even when nothing renders.
    hasAvailableFaresForService(
        service,
        direction,
        enableSplitTicketAmends,
        selectedComposition = this.getSelectedComposition(direction),
    ) {
        if (!service) {
            return false;
        }

        const fares = this.getCategoryFilteredFares(service);
        const { filteredSinglesFares, splits } = this.getFilteredFaresForAmend(
            fares,
            service,
            enableSplitTicketAmends,
            direction,
            selectedComposition,
        );

        return filteredSinglesFares.get('singles').size > 0 || splits.size > 0;
    }

    async getAmendTripRequestData(trip) {
        const ticketIds = this.getAmendedTicketIds();
        const isCurrentTripHasSplit = trip.isSplitTrip();
        const originalTrip = this.trips.first();
        const splitSalt = originalTrip?.splitSalt || generateUuidV4();

        const isAmendedTicketsHasSplits = Boolean(
            ticketIds.map(ticketId => this.getTripByTicketId(ticketId)).find(t => t?.isSplitTrip()),
        );

        // Backstop for cases the fare picker's own composition filtering (getFilteredFaresForAmend)
        // doesn't fully prevent (e.g. mid-selection, before both directions are finalised). Throwing
        // this exact error shape reuses AccountAmendOrder.nextStep()'s existing CORE-22699 handling -
        // same dedicated modal as the backend's own rejection, without the round trip.
        if (isCurrentTripHasSplit || isAmendedTicketsHasSplits) {
            // AccountAmendOrder.js relabels the return leg's fares onto "outward" on `trip`
            // before calling amendTrip when isAmendingInwardReturnOnly() is true, so read the
            // mismatch off whichever field on `trip` actually holds that direction's new data.
            const isReturnOnlyRelabeled = this.isAmendingInwardReturnOnly();
            const hasCompositionMismatch = [
                JourneyDirections.OUTWARD,
                JourneyDirections.INWARD,
            ].some(direction => {
                const selected = this.getSelectedComposition(direction);
                const newSplit =
                    direction === JourneyDirections.OUTWARD || isReturnOnlyRelabeled
                        ? trip.fares.outwardSplit
                        : trip.fares.returnSplit;
                const newComposition = newSplit?.fares?.first();
                return (
                    selected &&
                    newComposition &&
                    (newComposition.adults() !== selected.adults ||
                        newComposition.children() !== selected.children)
                );
            });

            if (hasCompositionMismatch) {
                throw {
                    body: {
                        errors: [
                            {
                                code: 0,
                                message:
                                    `${SPLIT_TRIP_PASSENGER_COUNT_MISMATCH_MESSAGE} ` +
                                    `${this.order?.id}`,
                            },
                        ],
                    },
                };
            }
        }

        const body = {
            amendedTickets: ticketIds.toJS(),
        };

        const { splitTrips, regularTrip: regularTripFromSplit } = await serialiseSplitTrips({
            trip,
            splitSalt,
        });
        const serialisedTrip = await serialiseTrip(trip);

        if (isCurrentTripHasSplit) {
            body.splitTrips = splitTrips;
            if (regularTripFromSplit) {
                body.regularTrip = regularTripFromSplit;
            }
        }
        if (!isCurrentTripHasSplit && isAmendedTicketsHasSplits) {
            body.regularTrip = serialisedTrip;
            body.splitTrips = [];
        }
        if (
            !isCurrentTripHasSplit &&
            !isAmendedTicketsHasSplits &&
            (trip.fares.outwardSingle || trip.fares.returnSingle || trip.fares.return)
        ) {
            body.trip = serialisedTrip;
        }

        return { body, isSplitAmend: isCurrentTripHasSplit || isAmendedTicketsHasSplits };
    }
}

export default AmendRequest;
